import type { SorterResult } from "antd/lib/table/interface";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { TerminalProfileList } from "src/app/features/TerminalProfile/types/TerminalProfileList";
import { type ExtendedColumnsType, GenericTable, type ResourceTableProps } from "../../../../../components";
import {
    displayDateTimeValue,
    displayStatusValue,
} from "../../../../../components/Table/components/PropertyTable/transformations/cellTransformations";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";

export const TerminalProfilesTable: FC<ResourceTableProps<TerminalProfileList>> = ({
    onStateChanged,
    onRowSelectionChange,
    datasource,
    resource,
}) => {
    const { t } = useTranslation();
    const getColumnsConfig = useCallback(
        (sortInfo: SorterResult<TerminalProfileList>): ExtendedColumnsType<TerminalProfileList>[] => {
            return [
                {
                    title: t("terminalProfile.id"),
                    key: "id",
                    dataIndex: "id",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "id" ? sortInfo.order : null,
                },
                {
                    title: t("terminalProfile.name"),
                    key: "name",
                    dataIndex: "name",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "name" ? sortInfo.order : null,
                },
                {
                    title: t("terminalProfileList.terminals"),
                    key: "totalTerminals",
                    dataIndex: "totalTerminals",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "totalTerminals" ? sortInfo.order : null,
                },
                {
                    title: t("terminalProfile.terminalType"),
                    key: "terminalType",
                    dataIndex: "terminalType",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "terminalType" ? sortInfo.order : null,
                    render: (value) => t(value),
                },
                {
                    title: t("terminalProfile.terminalTemplate"),
                    key: "terminalTemplate",
                    dataIndex: "terminalTemplate",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "terminalTemplate" ? sortInfo.order : null,
                },
                {
                    title: t("terminalProfile.status"),
                    key: "status",
                    dataIndex: "status",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "status" ? sortInfo.order : null,
                    render: (value) => displayStatusValue(value),
                },
                {
                    title: t("terminalProfile.changed"),
                    dataIndex: "changed",
                    key: "changed",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "changed" ? sortInfo.order : null,
                    render: (value) => displayDateTimeValue(value),
                },
            ];
        },
        [t],
    );

    const navigate = useNavigate();
    const onRowClick = (record: TerminalProfileList) => {
        navigate(`${BASE_PATH}/${ResourceName.TERMINAL_PROFILES}/${FORM_ACTION.EDIT}/${record.id}`);
    };

    return (
        (datasource && (
            <GenericTable<TerminalProfileList>
                name="terminalProfiles_table"
                getColumnsConfig={getColumnsConfig}
                datasource={datasource}
                onSelectionCallback={onRowSelectionChange}
                onStateChanged={onStateChanged}
                onRow={(record: TerminalProfileList) => {
                    return {
                        onClick: () => onRowClick(record),
                    };
                }}
                resource={resource}
            />
        )) ||
        null
    );
};
